/**
 * useUserProcedures — react-query hook returning the current user's
 * non-generic procedures. Used by the Simulador setup screen.
 *
 * "Generic" procedures (consulta / consultation / avaliação / retorno) are
 * filtered out client-side, mirroring the regex used by
 * `supabase/functions/simulator/start`.
 *
 * Implementation note: scopes by `profileId` (resolved via Clerk → profiles
 * mapping) and supplies an explicit `.eq("user_id", profileId)` rather than
 * relying solely on RLS. Mirrors `ProceduresFields` so the simulador setup
 * sees the same rows as `/perfil`.
 *
 * Spec: docs/architecture/simulador-spec.md §2.1.
 */
import { useQuery } from "@tanstack/react-query";
import { useSupabaseClient } from "@/lib/supabase";
import { useProfileId } from "@/features/onboarding/hooks/useProfileId";

export interface UserProcedureLite {
  id: string;
  name: string;
  value_cents: number | null;
  duration_minutes: number | null;
  format: string | null;
}

const GENERIC_RE = /^(consulta|consultation|avalia[cç][aã]o|retorno)$/i;

export function isGenericProcedure(name: string | null | undefined): boolean {
  if (!name) return true;
  return GENERIC_RE.test(name.trim());
}

export function useUserProcedures() {
  const { client, isReady: supabaseReady } = useSupabaseClient();
  const { profileId } = useProfileId();

  return useQuery<UserProcedureLite[]>({
    queryKey: ["simulador", "user-procedures", profileId],
    enabled: Boolean(profileId) && supabaseReady,
    staleTime: 30_000,
    queryFn: async () => {
      if (!profileId) return [];
      const { data, error } = await client
        .from("user_procedures")
        .select("id,name,value_cents,duration_minutes,format")
        .eq("user_id", profileId)
        .order("position", { ascending: true });
      if (error) throw error;
      return (data ?? []).filter((p) => !isGenericProcedure(p.name));
    },
  });
}
